import { useMemo, useState } from "react";
import { submitContactSubmission } from "../api/forms";

const subjectOptions = [
  "I would like to buy commercial real estate",
  "I would like to sell my commercial real estate",
  "I am looking for a commercial rental",
  "I want to rent out my commercial property",
  "I am looking for a commercial property manager",
  "I would like to buy land",
  "Other",
];

const purchaseReasonOptions = ["For my own use", "For rental income"];

const titleOptions = ["Mr.", "Mrs.", "Miss.", "Ms."];

const usStateOptions = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
  "American Samoa",
  "Guam",
  "Northern Mariana Islands",
  "Puerto Rico",
  "United States Minor Outlying Islands",
  "Virgin Islands, U.S.",
];

const countryOptions = [
  "United States of America (USA)",
  "Turkey",
  "Canada",
  "United Kingdom",
  "United Arab Emirates",
  "Australia",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "Ireland",
  "Portugal",
  "Poland",
  "Greece",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "India",
  "China, People's Republic of",
  "Japan",
  "South Korea",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Indonesia",
  "Philippines",
  "Mexico",
  "Brazil",
  "Argentina",
  "Chile",
  "Colombia",
  "South Africa",
  "Nigeria",
  "Kenya",
  "Egypt",
  "New Zealand",
  "Other",
];

const turkiyeProvinceOptions = [
  "Adana",
  "Adiyaman",
  "Afyonkarahisar",
  "Agri",
  "Aksaray",
  "Amasya",
  "Ankara",
  "Antalya",
  "Ardahan",
  "Artvin",
  "Aydin",
  "Balikesir",
  "Bartin",
  "Batman",
  "Bayburt",
  "Bilecik",
  "Bingol",
  "Bitlis",
  "Bolu",
  "Burdur",
  "Bursa",
  "Canakkale",
  "Cankiri",
  "Corum",
  "Denizli",
  "Diyarbakir",
  "Duzce",
  "Edirne",
  "Elazig",
  "Erzincan",
  "Erzurum",
  "Eskisehir",
  "Gaziantep",
  "Giresun",
  "Gumushane",
  "Hakkari",
  "Hatay",
  "Igdir",
  "Isparta",
  "Istanbul",
  "Izmir",
  "Kahramanmaras",
  "Karabuk",
  "Karaman",
  "Kars",
  "Kastamonu",
  "Kayseri",
  "Kirikkale",
  "Kirklareli",
  "Kirsehir",
  "Kilis",
  "Kocaeli",
  "Konya",
  "Kutahya",
  "Malatya",
  "Manisa",
  "Mardin",
  "Mersin",
  "Mugla",
  "Mus",
  "Nevsehir",
  "Nigde",
  "Ordu",
  "Osmaniye",
  "Rize",
  "Sakarya",
  "Samsun",
  "Sanliurfa",
  "Siirt",
  "Sinop",
  "Sirnak",
  "Sivas",
  "Tekirdag",
  "Tokat",
  "Trabzon",
  "Tunceli",
  "Usak",
  "Van",
  "Yalova",
  "Yozgat",
  "Zonguldak",
];

const finderOptions = [
  "Internet search",
  "A friend/relative recommended",
  "Online advertisement",
  "Printed advertisement",
  "From a real estate agent",
  "Other",
];

const initialForm = {
  subjects: [],
  firstName: "",
  lastName: "",
  preferredTitle: "",
  cellPhone: "",
  telephoneCountryCode: "",
  emailAddress: "",
  lookingState: "",
  lookingCity: "",
  purchaseReasons: [],
  ownedState: "",
  ownedCity: "",
  currentCountryOfResidence: "",
  usResidenceState: "",
  usResidenceCity: "",
  turkiyeResidenceProvince: "",
  turkiyeResidenceCity: "",
  howDidYouFindUs: "",
  additionalInformation: "",
};

const fieldLabelClass =
  "flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-obsidian-600";
const fieldClass =
  "rounded-2xl border border-obsidian-300 bg-cream-50 px-4 py-3 text-sm font-medium normal-case tracking-normal text-obsidian-900";

function ContactFormSection({
  title = "Send Us a Message",
  eyebrow = "Contact",
  description = "",
  className = "",
}) {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const isLookingInUs = useMemo(
    () =>
      formData.subjects.some((subject) =>
        [
          "I would like to buy commercial real estate",
          "I am looking for a commercial rental",
          "I would like to buy land",
        ].includes(subject)
      ),
    [formData.subjects]
  );

  const ownsPropertyInUs = useMemo(
    () =>
      formData.subjects.some((subject) =>
        [
          "I would like to sell my commercial real estate",
          "I want to rent out my commercial property",
          "I am looking for a commercial property manager",
        ].includes(subject)
      ),
    [formData.subjects]
  );

  const showPurchaseReasons = formData.subjects.includes("I would like to buy commercial real estate");
  const showUsResidence = formData.currentCountryOfResidence === "United States of America (USA)";
  const showTurkiyeResidence = formData.currentCountryOfResidence === "Turkey";

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCheckboxValue = (key, value) => {
    setFormData((prev) => {
      const current = prev[key];
      return {
        ...prev,
        [key]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await submitContactSubmission(formData);

      setStatus({
        type: "success",
        message: "Thank you. Your message has been sent successfully.",
      });
      setFormData(initialForm);
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || error.message || "Something went wrong while sending your message.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={`premium-panel w-full space-y-6 p-7 sm:p-10 ${className}`.trim()} onSubmit={handleSubmit}>
      <div>
        {/* <p className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-600">{eyebrow}</p> */}
        <h2 className="mt-3 font-serif text-4xl leading-tight text-obsidian-950">{title}</h2>
        {description ? <p className="mt-3 text-sm leading-7 text-obsidian-700">{description}</p> : null}
      </div>

      <label className={fieldLabelClass}>
        Subject (Select all that apply)
        <div className="grid gap-3 rounded-2xl border border-obsidian-300 bg-cream-50 p-4 text-sm normal-case tracking-normal text-obsidian-900">
          {subjectOptions.map((option) => (
            <label key={option} className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.subjects.includes(option)}
                onChange={() => toggleCheckboxValue("subjects", option)}
                className="mt-1 h-4 w-4"
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className={fieldLabelClass}>
          First name
          <input
            className={fieldClass}
            value={formData.firstName}
            onChange={(event) => updateField("firstName", event.target.value)}
            required
          />
        </label>

        <label className={fieldLabelClass}>
          Last name
          <input
            className={fieldClass}
            value={formData.lastName}
            onChange={(event) => updateField("lastName", event.target.value)}
            required
          />
        </label>

        <label className={fieldLabelClass}>
          Preferred title
          <select
            className={fieldClass}
            value={formData.preferredTitle}
            onChange={(event) => updateField("preferredTitle", event.target.value)}
            required
          >
            <option value="">Select an option</option>
            {titleOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className={fieldLabelClass}>
          Cell phone
          <input
            className={fieldClass}
            value={formData.cellPhone}
            onChange={(event) => updateField("cellPhone", event.target.value)}
            required
          />
        </label>

        <label className={fieldLabelClass}>
          Telephone number country code
          <input
            className={fieldClass}
            value={formData.telephoneCountryCode}
            onChange={(event) => updateField("telephoneCountryCode", event.target.value)}
            required
          />
        </label>

        <label className={fieldLabelClass}>
          Email Address
          <input
            type="email"
            className={fieldClass}
            value={formData.emailAddress}
            onChange={(event) => updateField("emailAddress", event.target.value)}
            required
          />
        </label>
      </div>

      {isLookingInUs ? (
        <div className="space-y-4 rounded-2xl border border-red-200 bg-red-50 p-5">
          <p className="text-sm font-medium text-red-700">
            You indicated above that you are looking for property(ies) in the US
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={fieldLabelClass}>
              The US state/territory you are looking for the property(ies)
              <select
                className={fieldClass}
                value={formData.lookingState}
                onChange={(event) => updateField("lookingState", event.target.value)}
                required={isLookingInUs}
              >
                <option value="">State/province</option>
                {usStateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className={fieldLabelClass}>
              The US city you are looking for the property(ies)
              <input
                className={fieldClass}
                value={formData.lookingCity}
                onChange={(event) => updateField("lookingCity", event.target.value)}
                required={isLookingInUs}
              />
            </label>
          </div>

          {showPurchaseReasons ? (
            <label className={fieldLabelClass}>
              Your reason to purchase a property(ies) in the US (Select all that apply)
              <div className="grid gap-3 rounded-2xl border border-obsidian-300 bg-cream-50 p-4 text-sm normal-case tracking-normal text-obsidian-900">
                {purchaseReasonOptions.map((option) => (
                  <label key={option} className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.purchaseReasons.includes(option)}
                      onChange={() => toggleCheckboxValue("purchaseReasons", option)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </label>
          ) : null}
        </div>
      ) : null}

      {ownsPropertyInUs ? (
        <div className="space-y-4 rounded-2xl border border-red-200 bg-red-50 p-5">
          <p className="text-sm font-medium text-red-700">
            You indicated above that you own property(ies) in the US
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={fieldLabelClass}>
              The US state/territory where your property(ies) are located in
              <select
                className={fieldClass}
                value={formData.ownedState}
                onChange={(event) => updateField("ownedState", event.target.value)}
                required={ownsPropertyInUs}
              >
                <option value="">State/province</option>
                {usStateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className={fieldLabelClass}>
              The US city your US property(ies) are located in
              <input
                className={fieldClass}
                value={formData.ownedCity}
                onChange={(event) => updateField("ownedCity", event.target.value)}
                required={ownsPropertyInUs}
              />
            </label>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className={`${fieldLabelClass} sm:col-span-2`}>
          Current country of residence
          <select
            className={fieldClass}
            value={formData.currentCountryOfResidence}
            onChange={(event) => updateField("currentCountryOfResidence", event.target.value)}
            required
          >
            <option value="">Select country</option>
            {countryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        {showUsResidence ? (
          <>
            <label className={fieldLabelClass}>
              Current state/province of residence in the US
              <select
                className={fieldClass}
                value={formData.usResidenceState}
                onChange={(event) => updateField("usResidenceState", event.target.value)}
                required={showUsResidence}
              >
                <option value="">State/province</option>
                {usStateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className={fieldLabelClass}>
              Current city of residence in the US
              <input
                className={fieldClass}
                value={formData.usResidenceCity}
                onChange={(event) => updateField("usResidenceCity", event.target.value)}
                required={showUsResidence}
              />
            </label>
          </>
        ) : null}

        {showTurkiyeResidence ? (
          <>
            <label className={fieldLabelClass}>
              Current province of residence in Türkiye
              <select
                className={fieldClass}
                value={formData.turkiyeResidenceProvince}
                onChange={(event) => updateField("turkiyeResidenceProvince", event.target.value)}
                required={showTurkiyeResidence}
              >
                <option value="">Province of residence in Türkiye</option>
                {turkiyeProvinceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className={fieldLabelClass}>
              Current city of residence in Türkiye
              <input
                className={fieldClass}
                value={formData.turkiyeResidenceCity}
                onChange={(event) => updateField("turkiyeResidenceCity", event.target.value)}
                required={showTurkiyeResidence}
              />
            </label>
          </>
        ) : null}

        <label className={`${fieldLabelClass} sm:col-span-2`}>
          How did you find us (Select one)
          <div className="grid gap-3 rounded-2xl border border-obsidian-300 bg-cream-50 p-4 text-sm normal-case tracking-normal text-obsidian-900 sm:grid-cols-2">
            {finderOptions.map((option) => (
              <label key={option} className="flex items-start gap-3">
                <input
                  type="radio"
                  name="howDidYouFindUs"
                  checked={formData.howDidYouFindUs === option}
                  onChange={() => updateField("howDidYouFindUs", option)}
                  className="mt-1 h-4 w-4"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </label>

        <label className={`${fieldLabelClass} sm:col-span-2`}>
          Additional information
          <textarea
            rows={6}
            className={fieldClass}
            value={formData.additionalInformation}
            onChange={(event) => updateField("additionalInformation", event.target.value)}
            required
          />
        </label>
      </div>

      {status.message ? (
        <div
          className={`rounded-2xl px-4 py-3 text-sm font-medium ${
            status.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
          }`}
        >
          {status.message}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        className="rounded-2xl bg-obsidian-950 px-5 py-3 text-sm font-semibold text-cream-50 transition hover:bg-obsidian-800 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {submitting ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
}

export default ContactFormSection;
